import { Component, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { TimelineModule } from 'primeng/timeline';
import { CardModule } from 'primeng/card';
import { switchMap, map, startWith, of, delay, catchError } from 'rxjs';
import { RentalAgreementsHistoryService } from './rental-agreement-history.service';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { AsyncPipe } from '@angular/common';
import { DateAgoPipe } from '@app/pipes/date-ago.pipe';
import { RaVersion, RentalAgreementStatusesDisplayNames } from '../rental-agreements.model';
import { RentalAgreementHistoryListItem } from './rental-agreement-history.model';
import { Router } from '@angular/router';

// Keep the timeline mounted for the accordion's collapse animation before clearing it.
const PANEL_COLLAPSE_ANIMATION_MS = 300;
const LOADING_STATE = { isLoading: true, isVisible: true, history: null, error: null };
const HIDDEN_STATE = { isLoading: false, isVisible: false, history: null, error: null };

@Component({
  selector: 'app-rental-agreement-history',
  standalone: true,
  imports: [TimelineModule, LoaderComponent, AsyncPipe, CardModule, DateAgoPipe],
  templateUrl: './rental-agreement-history.component.html',
  styleUrls: ['./rental-agreement-history.component.scss']
})
export class RentalAgreementHistoryComponent {
  private _raHistoryService = inject(RentalAgreementsHistoryService);
  private _router = inject(Router);

  rentalAgreementUid = input.required<string>();
  isVisible = input.required<boolean>();
  currentVersion = input<RaVersion>('latest');

  private _loadHistory$ = toObservable(this.rentalAgreementUid).pipe(
    switchMap((uid) => this._raHistoryService.getRentalAgreementHistory(uid)),
    map((history) => ({ isLoading: false, isVisible: true, history: this.toTimeline(history), error: null })),
    catchError((error: HttpErrorResponse) => of(this.toErrorState(error))),
    startWith(LOADING_STATE)
  );

  vm$ = toObservable(this.isVisible).pipe(
    switchMap((isVisible) =>
      isVisible ? this._loadHistory$ : of(HIDDEN_STATE).pipe(delay(PANEL_COLLAPSE_ANIMATION_MS))
    )
  );

  navigateToRaWithVersion(version: number, isLatest = false) {
    this._router.navigate(['/operations/rental-agreements'], {
      queryParams: { selected: this.rentalAgreementUid(), selectedVersion: isLatest ? null : version },
      queryParamsHandling: 'merge'
    });
  }

  private toTimeline(history: RentalAgreementHistoryListItem[]) {
    const newestFirst = [...history].reverse();
    return newestFirst.map((entry, index) => this.toTimelineEvent(entry, index, newestFirst));
  }

  private toTimelineEvent(
    entry: RentalAgreementHistoryListItem,
    index: number,
    newestFirst: RentalAgreementHistoryListItem[]
  ) {
    const olderEntry = newestFirst[index + 1];
    return {
      ...entry,
      changedDate: this.toUtcDate(entry.changedDate),
      statusDisplayName: RentalAgreementStatusesDisplayNames[Number(entry.rentalAgreementStatus)],
      showStatus: entry.rentalAgreementStatus !== olderEntry?.rentalAgreementStatus,
      isLatest: index === 0,
      isCurrentVersion: this.isCurrentVersion(entry.version, index)
    };
  }

  private isCurrentVersion(version: number, index: number): boolean {
    const current = this.currentVersion();
    return current === version || (index === 0 && (!current || current === 'latest'));
  }

  private toErrorState(error: HttpErrorResponse) {
    const message =
      error.status === 404
        ? 'No change history is available for this rental agreement.'
        : 'Failed to load the change history. Please try again.';
    return { isLoading: false, isVisible: true, history: null, error: message };
  }

  private toUtcDate(value: string): Date {
    return new Date(new Date(value) + 'Z');
  }
}
